import { cn } from '@/lib/utils'
import { Disclosure } from './Disclosure'
import { type ClaimsCapabilities, NO_INFORMATION } from './quoteCardTypes'
import { QuoteCardSection } from './quotesAnalytics'

interface ClaimsCapabilitiesSectionProps {
  /** Unset when we have no sourced claims data for the carrier. */
  claimsCapabilities?: ClaimsCapabilities
}

/** The prose rows, in display order. The booleans on ClaimsCapabilities drive the
    filters, not this table, so they're deliberately not listed here. */
const ITEMS: {
  label: string
  key: 'waysToFile' | 'supportAvailability' | 'payoutSpeed' | 'waysToGetPaid'
}[] = [
  { label: 'Ways to file', key: 'waysToFile' },
  { label: 'Availability', key: 'supportAvailability' },
  { label: 'Payout speed', key: 'payoutSpeed' },
  { label: 'Ways to get paid', key: 'waysToGetPaid' },
]

/**
 * Carrier claims-handling facts, sourced from carrier-published info — no
 * Cai-authored rating or opinion. The disclaimer lives here (not in the
 * card's mock data) so its wording can't drift between cards.
 */
export function ClaimsCapabilitiesSection({ claimsCapabilities }: ClaimsCapabilitiesSectionProps) {
  if (!claimsCapabilities)
    return (
      <Disclosure
        section={QuoteCardSection.CLAIMS_CAPABILITIES}
        label="Claims capabilities"
        summary={NO_INFORMATION}
      />
    )

  return (
    <Disclosure section={QuoteCardSection.CLAIMS_CAPABILITIES} label="Claims capabilities">
      <dl className="flex flex-col text-xs">
        {ITEMS.map((item) => {
          // Carrier profiles leave unknown facts blank, which rendered as an empty row.
          const value = claimsCapabilities[item.key] || NO_INFORMATION
          return (
            <div key={item.key} className="border-ink-50 flex items-baseline gap-3 border-b py-1.5">
              <dt className="text-foreground w-28 shrink-0 font-semibold">{item.label}</dt>
              <dd
                className={cn(
                  'text-left font-normal',
                  value === NO_INFORMATION ? 'text-ink-300' : 'text-muted-foreground'
                )}
              >
                {value}
              </dd>
            </div>
          )
        })}
      </dl>
    </Disclosure>
  )
}
